/** sessionStorage key for subdomain when on localhost (set from ?subdomain= or /support/:org/:token during impersonation) */
const LOCALHOST_SUBDOMAIN_KEY = 'alpha_customer_subdomain';

/** Platform org subdomains on *.localhost — not Assettech end-customer portals (e.g. da117hq.localhost). */
const NON_ASSETTECH_LOCALHOST_ORGS = new Set([
  'phoenix',
  'synetic',
  'drma',
  'drm',
  'recycle',
  'amwaste',
  'evolution',
  'nerdit',
  'dothavlu',
  'roctelecom',
]);

/** Staff org subdomain from route-based impersonation URLs: /support/:subdomain/:token */
function getOrgSubdomainFromSupportPathname(): string | null {
  try {
    const pathname = window.location.pathname.replace(/\/$/, '');
    const m = pathname.match(/^\/support\/([^/]+)\/([^/]+)$/);
    if (!m) return null;
    const org = m[1]?.trim()?.toLowerCase();
    return org || null;
  } catch {
    return null;
  }
}

/**
 * Call on the support/impersonation page before navigation strips ?subdomain= so API headers
 * keep using the staff org (e.g. phoenix), not the customer hostname (e.g. 3jxxvnj2.localhost).
 * Also persists the first path segment for /support/:subdomain/:token links.
 */
export function persistOrgSubdomainFromCurrentUrl(): void {
  try {
    const fromPath = getOrgSubdomainFromSupportPathname();
    if (fromPath) {
      sessionStorage.setItem(LOCALHOST_SUBDOMAIN_KEY, fromPath);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('subdomain')?.trim()?.toLowerCase();
    if (fromQuery) {
      sessionStorage.setItem(LOCALHOST_SUBDOMAIN_KEY, fromQuery);
    }
  } catch {
    // ignore
  }
}

/**
 * Determines the organization type based on the current URL
 * This is separate from subdomain detection and focuses on functionality/organization type
 */
export function isDrmaOrganization(): boolean {
  const subdomain = getCustomerSubdomain()?.toLowerCase();
  return subdomain === 'drma' || subdomain === 'drm';
}

export function isSyneticOrganization(): boolean {
  const subdomain = getCustomerSubdomain()?.toLowerCase();
  return subdomain === 'synetic';
}

export function getOrganizationType(): 'assettech' | 'default' {
  const hostname = window.location.hostname;

  // atportal.co.uk is always Assettech
  if (hostname === 'atportal.co.uk' || hostname.endsWith('.atportal.co.uk')) {
    return 'assettech';
  }

  // Bare localhost / 127.0.0.1: org type from stored subdomain (set by impersonation ?subdomain=)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const sub = getCustomerSubdomain();
    return sub === 'assettech' ? 'assettech' : 'default';
  }

  // *.localhost: Assettech customer portals use arbitrary subdomains (e.g. da117hq.localhost).
  // Only known non-Assettech platform orgs should resolve to default.
  if (hostname.endsWith('.localhost')) {
    const sub = getCustomerSubdomain()?.toLowerCase();
    if (sub === 'assettech') return 'assettech';
    if (sub && !NON_ASSETTECH_LOCALHOST_ORGS.has(sub)) return 'assettech';
    return 'default';
  }

  return 'default';
}

/**
 * Reads org subdomain from query or sessionStorage (set during impersonation in local dev).
 * Returns null if not on localhost or nothing stored.
 */
function getLocalhostSubdomainFromQueryOrStorage(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('subdomain')?.trim()?.toLowerCase();
    if (fromQuery) {
      sessionStorage.setItem(LOCALHOST_SUBDOMAIN_KEY, fromQuery);
      return fromQuery;
    }
    const fromPath = getOrgSubdomainFromSupportPathname();
    if (fromPath) {
      sessionStorage.setItem(LOCALHOST_SUBDOMAIN_KEY, fromPath);
      return fromPath;
    }
    const fromStorage = sessionStorage.getItem(LOCALHOST_SUBDOMAIN_KEY);
    if (fromStorage) return fromStorage;
  } catch {
    // ignore
  }
  return null;
}

/**
 * Gets the actual customer subdomain from the URL
 * This is used for API headers and customer identification.
 * In local dev (localhost or *.localhost), prefers ?subdomain= from backend (org for theme) then sessionStorage, so impersonation shows the correct org theme even when the host is the customer subdomain (e.g. dothavlu.localhost).
 */
export function getCustomerSubdomain(): string | null {
  const host = window.location.host;
  const hostname = window.location.hostname;

  const isBareLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isSubdomainLocalhost = hostname.endsWith('.localhost');

  // Local dev: prefer backend-passed org (query param) then sessionStorage, then hostname
  if (isBareLocalhost || isSubdomainLocalhost) {
    const fromQueryOrStorage = getLocalhostSubdomainFromQueryOrStorage();
    if (fromQueryOrStorage) return fromQueryOrStorage;
    if (isBareLocalhost) return 'assettech';
    // *.localhost with no query/storage: use hostname (e.g. synetic.localhost -> synetic)
    const sub = hostname.split('.')[0];
    return sub === 'www' ? 'assettech' : sub;
  }

  // Special handling for atportal.co.uk - extract the actual subdomain
  if (hostname === 'atportal.co.uk' || hostname.endsWith('.atportal.co.uk')) {
    if (hostname === 'atportal.co.uk') {
      return 'assettech'; // Root domain maps to assettech
    }
    // Extract subdomain from atportal.co.uk
    const subdomain = hostname.split('.')[0];
    return subdomain === 'www' ? 'assettech' : subdomain;
  }

  // Special handling for synetic.tech - always return synetic
  if (hostname === 'synetic.tech' || hostname.endsWith('.synetic.tech')) {
    if (hostname === 'synetic.tech') {
      return 'synetic'; // Root domain maps to synetic
    }
    const subdomain = hostname.split('.')[0];
    return subdomain === 'www' ? 'synetic' : 'synetic';
  }

  // Special handling for drmw.tech - always return drma (DRM America)
  if (hostname === 'drmw.tech' || hostname.endsWith('.drmw.tech')) {
    if (hostname === 'drmw.tech') {
      return 'drma'; // Root domain maps to drma
    }
    const subdomain = hostname.split('.')[0];
    return subdomain === 'www' ? 'drma' : 'drma';
  }

  // Special handling for roctelecom.app — customer hosts like icbs.roctelecom.app
  if (hostname === 'roctelecom.app' || hostname.endsWith('.roctelecom.app')) {
    return 'roctelecom';
  }

  // Extract subdomain for other domains
  const arr = host.split(".").slice(0, host.includes("localhost") ? -1 : -2);
  if (arr.length > 0) {
    return host.includes("vercel.app")
      ? localStorage.getItem("subdomain") || "recycle"
      : arr[0];
  }
  
  return null;
}
